type SessionTokens = {
  accessToken: string | null;
  csrfToken: string | null;
};

let session: SessionTokens = {
  accessToken: null,
  csrfToken: null,
};
interface RefreshedSession {
  role: string | null;
}

let refreshPromise: Promise<RefreshedSession | null> | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
    public readonly retryable: boolean,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function setSessionTokens(tokens: SessionTokens): void {
  session = tokens;
}

export function getSessionTokens(): SessionTokens {
  return session;
}

function errorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const value = body as Record<string, unknown>;
  if (typeof value.message === "string") return value.message;
  if (typeof value.error === "string") return value.error;
  if (value.error && typeof value.error === "object") {
    const nested = value.error as Record<string, unknown>;
    if (typeof nested.message === "string") return nested.message;
  }
  return fallback;
}

async function parseBody(response: Response): Promise<unknown> {
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return null;
  return response.json().catch(() => null);
}

function roleFromJwt(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const value = JSON.parse(atob(normalized)) as { role?: unknown };
    return typeof value.role === "string" ? value.role : null;
  } catch {
    return null;
  }
}

export async function refreshAccessToken(): Promise<RefreshedSession | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/v2/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        setSessionTokens({ accessToken: null, csrfToken: null });
        return null;
      }
      const body = (await response.json()) as { access_token: string };
      setSessionTokens({ accessToken: body.access_token, csrfToken: null });

      const csrfResponse = await fetch("/api/v2/auth/csrf-token", {
        credentials: "include",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${body.access_token}`,
        },
      });
      if (csrfResponse.ok) {
        const csrf = (await csrfResponse.json()) as { csrf_token: string };
        setSessionTokens({
          accessToken: body.access_token,
          csrfToken: csrf.csrf_token,
        });
      }
      return { role: roleFromJwt(body.access_token) };
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  retryAuth = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (session.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (
    session.csrfToken &&
    init.method &&
    !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase())
  ) {
    headers.set("X-CSRF-Token", session.csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    throw new ApiError("Nincs hálózati kapcsolat", 0, null, true);
  }

  if (response.status === 401 && retryAuth && (await refreshAccessToken())) {
    return apiFetch<T>(path, init, false);
  }

  const body = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(
      errorMessage(body, "A kérés nem sikerült"),
      response.status,
      body,
      response.status === 0 || response.status >= 500,
    );
  }

  return body as T;
}
